import React from 'react';
import { useUploadImageMutation } from '../../store/Api';
import { IsMutationSuccess } from '../../logic/MutationTypeChecker';
import { IUploadFileResponse } from '../../dal/BaseDAL';
import {
    Shimmer,
    ShimmerElementsGroup,
    ShimmerElementType,
    Image,
    ImageFit,
    IconButton,
    MessageBar,
    MessageBarType,
    ActionButton
} from '@fluentui/react';
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import {SerializedError} from "@reduxjs/toolkit";
import { useAssetSource } from '../Storefront/Assets/AssetSourceContext';
import AssetSourceMenu, { IAssetSourceMenuHandle } from '../Storefront/Assets/AssetSourceMenu';
import AssetPickerPanel from '../Storefront/Assets/AssetPickerPanel';
import { useUploadStorefrontAssetMutation } from '../../store/endpoints/StorefrontAssetEndpoints';
import { IStorefrontAssetUploadResponse } from '../../models/Storefront';

interface IImageUploaderProps {
    value?: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    label?: string;
    width?: number | string;
    height?: number | string;
    onClear?: () => void;
    imageId?: string;
    imageType?: string;
    customUploader?: (req: {imageId: string, file: ArrayBuffer, imageType: string}) => Promise<{ data?: IUploadFileResponse; error?: FetchBaseQueryError | SerializedError }>;
}

const ImageUploader: React.FC<IImageUploaderProps> = (props) => {
    const [uploadImage] = useUploadImageMutation();
    const [uploadStorefrontAsset] = useUploadStorefrontAssetMutation();
    // Set only under an AssetSourceContext provider (the storefront settings screens). Everywhere
    // else — product segments, categories — this stays undefined and the component behaves exactly
    // as it always has. A caller-supplied customUploader also wins: it owns where the bytes go.
    const assetSource = useAssetSource();
    const useAssetLibrary = !!assetSource && !props.customUploader;
    const menuRef = React.useRef<IAssetSourceMenuHandle>(null);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [saveSuccess, setSaveSuccess] = React.useState(false);
    const [saveMessage, setSaveMessage] = React.useState<string | undefined>();
    const [width, setWidth] = React.useState<number | string | undefined>(props.width);
    const [height, setHeight] = React.useState<number | string | undefined>(props.height ?? 150);

    React.useEffect(() => {
        setWidth(props.width);
        setHeight(props.height ?? 150);
    }, [props.width, props.height]);

    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const onButtonClick = () => {
        if (props.disabled) return;
        // With an asset library available the image/button is a chooser, not a file dialog: pick an
        // existing asset or upload a new one (which still ends up in the library).
        if (useAssetLibrary) {
            menuRef.current?.open();
            return;
        }
        inputRef?.current?.click();
    }

    const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            const f = reader.result;
            if (!f || typeof f === 'string') return;
            if (file.size > 20971520) {
                setSaveMessage("File size exceeds 20 MB");
                setSaveSuccess(false);
                return;
            }
            setSaving(true);

            if (useAssetLibrary && assetSource) {
                // Registered in the storefront asset library instead of the generic image store.
                // Keeps the file's REAL extension (the legacy path hardcodes .png) and seeds the
                // asset name from the uploaded file.
                const extension = file.name.split('.').pop() || 'png';
                const assetResponse = await uploadStorefrontAsset({
                    file: f,
                    fileName: `${props.imageId ?? crypto.randomUUID()}.${extension}`,
                    storefront: assetSource.storefrontId,
                    name: file.name.replace(/\.[^./\\]+$/, ''),
                });
                const assetData = (assetResponse as { data?: IStorefrontAssetUploadResponse }).data;
                const assetUrl = assetData?.asset?.url ?? assetData?.url;
                if (assetUrl) {
                    setSaveMessage('Image uploaded successfully');
                    setSaveSuccess(true);
                    props.onChange(assetUrl);
                } else {
                    console.error(['FAILED UPLOAD', assetResponse])
                    setSaveMessage('Failed to upload image');
                    setSaveSuccess(false);
                }
                setSaving(false);
                return;
            }

            const payload = {
                imageId: props.imageId ?? crypto.randomUUID(),
                file: f,
                imageType: props.imageType ?? 'Custom'
            }
            let response;
            if (props.customUploader) {
                response = await props.customUploader(payload);
            } else {
                response = await uploadImage(payload);
            }
            if (IsMutationSuccess<IUploadFileResponse>(response) && response.data?.url) {
                setSaveMessage('Image uploaded successfully');
                setSaveSuccess(true);
                props.onChange(response.data.url);
            } else {
                console.error(['FAILED UPLOAD', response])
                setSaveMessage('Failed to upload image');
                setSaveSuccess(false);
            }
            setSaving(false);

        };
        reader.readAsArrayBuffer(file);
    }
    
    const getShimmerHeight = () => {
        
        if(typeof height === 'string'){
            return 150
        }
        else if (typeof height === 'number'){
            return height
        }
        return 150
    }

    const getCustomElements = (): JSX.Element => {
        return (
            <div>
                <ShimmerElementsGroup
                    shimmerElements={[
                        { type: ShimmerElementType.line, width: (width ?? 150), height: getShimmerHeight()},
                    ]}
                />
            </div>
        );
    };


    // Unchanged visuals — the asset menu only wraps this in a relative span and overlays a chevron.
    const imageControl = props.value ? <Image
        src={props.value}
        width={width}
        height={height}
        // Only a frame with BOTH dimensions distorts: Fluent's no-imageFit styling forces the img to
        // 100%/100% with no object-fit, so anything whose aspect ratio doesn't match the frame is
        // stretched (Image.styles, the `width && height` branch). `contain` keeps that frame and adds
        // objectFit: contain, so the image fits it without distortion.
        //
        // Deliberately NOT applied when only a height is given (the segment/category icon editors,
        // and every caller that omits width). That case already preserves aspect ratio via Fluent's
        // `!width && height` branch — width auto — and the frame has no width of its own, so forcing
        // width: 100% would stretch the FRAME to the container and leave the icon floating in the
        // middle of it. Nothing to fix there, so nothing changes there.
        imageFit={width != null && height != null ? ImageFit.contain : undefined}
        onClick={onButtonClick}
        className={props.disabled ? 'product-image' : 'product-image-edit'}
        title={props.disabled ? props.label : 'Click to change image'}
    />
        :
    <ActionButton
        label={'Add Image'}
        iconProps={{ iconName: 'Add' }}
        title="Add Image"
        onClick={onButtonClick}
    >Apply Image</ActionButton>;

    return <div style={{marginBottom: '2em'}}>
        <input
            type="file"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={onChangeFile}
            accept='.gif,.png,.jpg,.jpeg,.webp,.svg,image/svg+xml'
        />
        {useAssetLibrary && assetSource && <AssetPickerPanel
            isOpen={pickerOpen}
            onDismiss={() => setPickerOpen(false)}
            storefrontId={assetSource.storefrontId}
            onSelect={(asset) => {
                setPickerOpen(false);
                props.onChange(asset.url);
            }}
        />}
        <div className='image-uploader-frame' style={{width, height, position: 'relative'}}>
            <Shimmer isDataLoaded={!saving} customElementsGroup={getCustomElements()}>
                {useAssetLibrary
                    ? <AssetSourceMenu
                        ref={menuRef}
                        disabled={props.disabled}
                        onSelectAsset={() => setPickerOpen(true)}
                        onUploadNew={() => inputRef?.current?.click()}
                    >
                        {imageControl}
                    </AssetSourceMenu>
                    : imageControl}
                {/* Revealed on hover, and on keyboard focus, by .image-uploader-frame in App.css. It sits
                    on top of the image it deletes, so at rest it is a delete button covering part of the
                    thing being looked at — and on a small frame it covers most of it. Kept mounted rather
                    than conditionally rendered so it stays in the tab order. */}
                {props.onClear && props.value && <IconButton
                    className='image-uploader-clear'
                    iconProps={{ iconName: 'Delete' }}
                    title='Remove image'
                    ariaLabel='Remove image'
                    onClick={props.onClear}
                    style={{ position: 'absolute', top: 10, right: 10 }}
                />}
            </Shimmer>
            {saveMessage && <MessageBar
                style={{ zIndex: '1000', width: 'fit-content' }}
                messageBarType={saveSuccess ? MessageBarType.success : MessageBarType.error}
                onDismiss={() => setSaveMessage(undefined)}
                dismissButtonAriaLabel="Close"
                dismissIconProps={{ iconName: 'Cancel' }}
                >
                {saveMessage}
            </MessageBar>}
        </div>
    </div>
}

export default ImageUploader;